import React, {
    type ReactNode,
    useCallback,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import {
    type LoadFunc,
    type TableHooks,
    useTableHooksFromStore,
} from '../../packages/blueprint-components/src/useTableHooks'
import type { Column } from '../../types/table'
import { useSavedFiltersStore } from '../../hooks/useSavedFiltersStore'
import { userStore } from '../../stores/user_store'
import { AgGridTable, DummyHeader, type AgGridColumnDef } from '../table2/AgGridTable'
import { Columns } from '../table2/ad_grid_columns'
import { TableStore } from '../table2/table_store'
import type { FilterI } from '../../constants/filter'
import type { SavedFiltersStore } from '../table2/Filter3'
import { capitalCase } from 'change-case'

/**
 * Narrow bridge for the two recipe-list routes while their domain renderers are
 * moved to AG Grid.  It deliberately has no consumers outside Recipes and
 * Segments: shared request/selection/filter ownership belongs here, while the
 * pages retain their endpoint-specific dialogs and mutations.
 */
export const useRecipeListTable = <T,>(name: string, load: LoadFunc<T>, requestContext?: any) => {
    const [store] = useState(() => new TableStore<T>(userStore, name))
    const savedFiltersStore = useSavedFiltersStore()
    const tableHooks = useTableHooksFromStore(load, {}, false, store, requestContext, {
        requireProfile: false,
    })
    return { store, savedFiltersStore, tableHooks }
}

/** Match the legacy table's readable fallback for keys such as `last_change`. */
export const legacyColumnHeader = (key: string): string => capitalCase(key.replaceAll('_', ' '))

const recipeColumnSizing: Record<string, Pick<AgGridColumnDef, 'width' | 'minWidth' | 'flex'>> = {
    name: { minWidth: 160, flex: 1 },
    enabled: { width: 96 },
    ad_type: { width: 110 },
    trigger: { minWidth: 220, flex: 2 },
    frequency: { width: 130 },
    action: { minWidth: 120, flex: 1 },
    last_change: { width: 125 },
    last_run: { width: 125 },
    ' ': { width: 56 },
}

/**
 * Preserves the legacy recipe-list column contract while moving its rendering
 * into AG Grid. Keeping this adapter pure makes parity behaviour testable.
 */
export const recipeColumnsToAgGrid = <T,>(
    columns: Column<T, any>[],
    mutateItems: TableHooks<T>['mutateItems'],
    reload: TableHooks<T>['reload'],
): AgGridColumnDef<T>[] =>
    columns.map((column) => {
        const sortable = column.sortable !== false && !column.noRowClick && column.key !== ' '
        const renderCell = (params: any) =>
            column.cell?.(params.data, {
                mutateItems,
                reload,
            })

        const sizing = recipeColumnSizing[column.key]

        return {
            field: column.key as keyof T,
            headerName:
                column.text_title ??
                (typeof column.title === 'string' ? column.title : legacyColumnHeader(column.key)),
            headerNode: typeof column.title === 'string' ? undefined : column.title,
            headerComponent: sortable ? DummyHeader : undefined,
            align: (column.headerStyle as any)?.justifyContent === 'flex-end' ? 'right' : 'left',
            cellClass: (column.style as any)?.textAlign === 'right' ? 'justify-end' : undefined,
            width: column.width ?? sizing?.width,
            minWidth: sizing?.minWidth,
            cellRenderer: column.noRowClick
                ? (params: any) => <div data-no-row-click>{renderCell(params)}</div>
                : renderCell,
            flex: sizing?.flex,
        }
    })

export const RecipeListAgGrid = <T,>({
    name,
    store,
    savedFiltersStore,
    tableHooks,
    columns,
    filters,
    createAction,
    className,
    cardClassName,
    nested,
    fitParent = false,
}: {
    name: string
    store: TableStore<T>
    savedFiltersStore: SavedFiltersStore
    tableHooks: TableHooks<T>
    columns: Column<T, any>[]
    filters: FilterI[]
    createAction: ReactNode
    className?: string
    cardClassName?: string
    nested?: boolean
    /** Use the Layout's bounded content area as the grid viewport. */
    fitParent?: boolean
}) => {
    // tableHooks.mutateItems/.reload change identity every render, which
    // would rebuild gridColumns' cellRenderer closures every render too and
    // defeat AG Grid's columnDefs stabilization (MER-3119). Route through a
    // ref so the exposed callbacks stay stable but still call the latest impl.
    // Update the refs in a layout effect, not during render — React 18's
    // createRoot can render without committing, and mutating a ref in the
    // render body would let an in-flight callback pick up an uncommitted one.
    const mutateItemsRef = useRef(tableHooks.mutateItems)
    const reloadRef = useRef(tableHooks.reload)
    useLayoutEffect(() => {
        mutateItemsRef.current = tableHooks.mutateItems
        reloadRef.current = tableHooks.reload
    })

    const stableMutateItems = useCallback<TableHooks<T>['mutateItems']>(
        (callback) => mutateItemsRef.current(callback),
        [],
    )
    const stableReload = useCallback<TableHooks<T>['reload']>(() => reloadRef.current(), [])

    const gridColumns = useMemo<AgGridColumnDef<T>[]>(
        () => [
            Columns.Select as AgGridColumnDef<T>,
            ...recipeColumnsToAgGrid(columns, stableMutateItems, stableReload),
        ],
        [columns, stableMutateItems, stableReload],
    )

    return (
        <AgGridTable
            name={name}
            showHeader={false}
            className={className}
            cardClassName={cardClassName}
            tableHooks={tableHooks}
            store={store}
            savedFiltersStore={savedFiltersStore}
            savedFiltersTableName={name.toLowerCase()}
            filters={filters}
            search
            searchPlaceholder="Search by name or trigger"
            columns={gridColumns}
            selectKey={'id' as keyof T}
            stableRowId
            mutableFields={['enabled', 'recipe-delete', 'recipes-bulk-run']}
            bulkActions
            columnSelection
            dateSelect={false}
            resizableColumns
            otherActions={createAction}
            nested={nested}
            fitParent={fitParent}
        />
    )
}
